import { Center, type MantineColor, RingProgress } from "@mantine/core";
import type { PropsWithChildren } from "react";
import { useLayoutEffect, useState } from "react";
import classes from "./avatar-styles.module.css";
import { RippleAnimation } from "./RippleAnimation";

const PLAY_RIPPLE_INTERVAL = 30;

/**
 * Animates from an initial value to 100% after browser paint.
 * Uses useLayoutEffect + double requestAnimationFrame to ensure initial state renders before animation starts.
 */
function useRingAnimation(initialPercentage: number, duration: number, enabled: boolean) {
    const [value, setValue] = useState(initialPercentage);
    const [transitionMs, setTransitionMs] = useState(0);

    useLayoutEffect(() => {
        setTransitionMs(0);
        setValue(initialPercentage);

        if (!enabled) return;

        // Double rAF ensures we're past the paint frame before triggering animation
        const handles: number[] = [
            requestAnimationFrame(() => {
                handles.push(
                    requestAnimationFrame(() => {
                        setTransitionMs(duration);
                        setValue(100);
                    }),
                );
            }),
        ];

        return () => handles.forEach(cancelAnimationFrame);
    }, [initialPercentage, duration, enabled]);

    return { value, transitionMs };
}

interface TimerProps {
    duration?: number;
    initialPercentage?: number;
}

interface RingProps {
    scaleRatio: number;
    size: number;
    thickness: number;
}

interface NotificationTimerRingProps {
    timer: TimerProps;
    variantColor: MantineColor;
    withRippleAnimation?: boolean;
    ring: RingProps;
}

export function NotificationTimerRing({
    variantColor,
    timer: { initialPercentage = 0, duration = 0 },
    withRippleAnimation,
    ring: { size: ringSize, thickness, scaleRatio },
    children,
}: PropsWithChildren<NotificationTimerRingProps>) {
    const showRing = duration > 0;
    const ringColor = showRing ? "var(--mantine-color-dark-9)" : "transparent";
    const { value: ringValue, transitionMs } = useRingAnimation(initialPercentage, duration, showRing);

    return (
        <Center
            style={{
                position: "relative",
                width: ringSize,
                height: ringSize,
                minWidth: ringSize,
                minHeight: ringSize,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
            }}
        >
            {showRing && withRippleAnimation && (
                <RippleAnimation color={variantColor} repeatInterval={PLAY_RIPPLE_INTERVAL} size={ringSize} />
            )}
            <RingProgress
                className={classes.notification_ring}
                roundCaps={showRing}
                rootColor={ringColor}
                size={ringSize}
                thickness={thickness}
                transitionDuration={transitionMs}
                sections={[{ value: ringValue, color: variantColor }]}
                styles={{
                    svg: {
                        display: "block",
                        margin: "auto",
                        position: "absolute",
                        top: 0,
                        left: 0,
                        right: 0,
                        bottom: 0,
                        transform: `scale(${scaleRatio}, -${scaleRatio}) rotate(45deg)`,
                    },
                    curve: {
                        transitionTimingFunction: "linear",
                        transitionProperty: "stroke-dasharray, stroke-dashoffset",
                    },
                    label: {
                        insetInline: 0,
                        className: classes.notification_ring_label,
                    },
                    root: {
                        width: "100%",
                        height: "100%",
                        position: "absolute",
                        top: 0,
                        left: 0,
                        right: 0,
                        bottom: 0,
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                    },
                }}
                label={children}
            />
        </Center>
    );
}
